import { useEffect, useState } from 'react';

import { Alert, Box, Slider, Snackbar, Tooltip, Typography } from '@mui/material';
import BoltIcon from '@mui/icons-material/Bolt';
import LocalFireDepartmentIcon from '@mui/icons-material/LocalFireDepartment';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import LandscapeIcon from '@mui/icons-material/Landscape';
import RadarIcon from '@mui/icons-material/Radar';

import {
    NotificationSubscriptionUpdate,
    useProjectNotificationSubscriptions,
    useUpdateNotificationSubscription,
} from '../../api/notificationSubscriptions';
import ProjectRecipientsTable, {
    RecipientKind,
} from '../notifications/ProjectRecipientsTable';
import { useAuth } from '../../contexts/AuthContext';
import { formatDistance } from '../../utils/units';

const KINDS: RecipientKind[] = [
    {
        kind: 'hotspot',
        label: 'Satellite hotspots',
        icon: <LocalFireDepartmentIcon sx={{ fontSize: 18, color: '#ff6b35' }} />,
    },
    {
        kind: 'lightning',
        label: 'Lightning strikes',
        icon: <BoltIcon sx={{ fontSize: 18, color: '#a78bfa' }} />,
    },
    {
        kind: 'hazard',
        label: 'Hazards incidents',
        icon: <WarningAmberIcon sx={{ fontSize: 18, color: '#f59e0b' }} />,
    },
    {
        kind: 'ignition',
        label: 'Ignition points',
        icon: <WhatshotIcon sx={{ fontSize: 18, color: '#ff2d95' }} />,
    },
    {
        kind: 'burnt_area',
        label: 'Burnt areas',
        icon: <LandscapeIcon sx={{ fontSize: 18, color: '#b91c1c' }} />,
    },
];

const NO_SOURCE_NOTE =
    'no data source is configured, add one in Data Management first';
const NOT_ENABLED_NOTE =
    'not enabled for your organisation, contact FiSci to have it switched on';

const labelSx = {
    color: '#94a3b8',
    fontWeight: 600,
    textTransform: 'uppercase' as const,
    fontSize: '0.65rem',
};

interface NotificationsTabProps {
    /** Passed explicitly — useParams breaks when opened from the listing page. */
    projectId?: string;
}

/**
 * Who receives this project's realtime detection alerts, and how far beyond
 * the boundary a detection still counts.
 *
 * One table for everyone (the viewer's own row included), and one alert
 * radius for the whole project: the people watching a project should all be
 * hearing about the same fires, so the radius is an admin's call rather
 * than each subscriber's.
 */
export default function NotificationsTab({ projectId }: NotificationsTabProps) {
    const { user } = useAuth();
    const { data: settings } = useProjectNotificationSubscriptions(projectId);
    const updateSubscription = useUpdateNotificationSubscription(
        projectId,
        user?.id
    );
    const unitSystem = user?.unit_system ?? 'metric';

    // Radius mid-drag; committed on release, cleared once the server echoes.
    const [draftRadius, setDraftRadius] = useState<number | null>(null);
    const [snackbar, setSnackbar] = useState('');
    const serverRadius = settings?.data?.radius_km;
    useEffect(() => {
        setDraftRadius(null);
    }, [serverRadius]);

    if (!settings?.data) return null;
    const {
        available_kinds,
        configured_kinds,
        max_radius_km,
        users = [],
        can_manage = false,
        radius_km,
    } = settings.data;

    const onError = (error: { message?: string } | null) =>
        setSnackbar(
            error?.message && error.message !== 'Request failed'
                ? `Failed to update notifications: ${error.message}`
                : 'Failed to update notifications'
        );

    // The caller's own row goes through the self path (no user_id) so the
    // backend rule — only admins may name someone else — never trips on a
    // user editing themselves.
    const patchUser = (userId: number, update: NotificationSubscriptionUpdate) =>
        updateSubscription.mutate(
            userId === user?.id
                ? { subscriptions: update }
                : { subscriptions: update, user_id: userId },
            { onError }
        );

    const commitRadius = (value: number) => {
        if (value === radius_km) {
            setDraftRadius(null);
            return;
        }
        updateSubscription.mutate({ radius_km: value }, { onError });
    };

    // Kinds anyone here could be alerted about get a column; the rest get a
    // one-line explanation so the absence isn't a mystery.
    const usableKinds = KINDS.filter(({ kind }) => available_kinds[kind] !== false);
    const blockedKinds = KINDS.filter(({ kind }) => available_kinds[kind] === false);
    const shownRadius = draftRadius ?? radius_km;

    return (
        <Box sx={{ p: 2 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    justifyContent: 'space-between',
                    gap: 3,
                    mb: 2,
                    flexWrap: 'wrap',
                }}
            >
                <Box sx={{ flex: '1 1 380px', minWidth: 280 }}>
                    <Typography
                        variant="h6"
                        sx={{ fontWeight: 500, color: '#fff', mb: 0.5 }}
                    >
                        Notifications
                    </Typography>
                    <Typography
                        sx={{ color: 'rgba(255, 255, 255, 0.6)', fontSize: '0.85rem' }}
                    >
                        Who is alerted when new detections arrive near this project.
                        One summary message per batch of detections, with a cooldown
                        so an ongoing event doesn't message anyone every few minutes.{' '}
                        {can_manage
                            ? 'As an administrator you can change any row and the alert radius.'
                            : "You can change your own row; an administrator can change anyone's."}
                    </Typography>
                </Box>

                {/* Project-level alert radius. Members see the value; only an
                    admin gets the slider, since widening it pages everyone. */}
                <Box
                    sx={{
                        flex: '0 1 300px',
                        minWidth: 240,
                        p: 1.5,
                        border: '1px solid rgba(255,255,255,0.1)',
                        borderRadius: 1,
                        backgroundColor: 'rgba(255,255,255,0.03)',
                    }}
                >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <RadarIcon sx={{ fontSize: 16, color: '#00d4ad' }} />
                        <Typography sx={labelSx}>
                            Alert radius · {formatDistance(shownRadius, unitSystem)}
                        </Typography>
                    </Box>
                    {can_manage ? (
                        <Slider
                            size="small"
                            value={shownRadius}
                            min={0}
                            max={max_radius_km}
                            step={1}
                            onChange={(_, value) => setDraftRadius(value as number)}
                            onChangeCommitted={(_, value) =>
                                commitRadius(value as number)
                            }
                            aria-label="Alert radius"
                            sx={{
                                color: '#00d4ad',
                                mt: 0.5,
                                '& .MuiSlider-thumb': { width: 12, height: 12 },
                            }}
                        />
                    ) : (
                        <Tooltip title="Only an administrator can change the project's alert radius">
                            <Slider
                                size="small"
                                value={radius_km}
                                min={0}
                                max={max_radius_km}
                                disabled
                                aria-label="Alert radius (read only)"
                                sx={{
                                    mt: 0.5,
                                    '& .MuiSlider-thumb': { width: 12, height: 12 },
                                    '&.Mui-disabled': { color: '#0f8f77' },
                                }}
                            />
                        </Tooltip>
                    )}
                    <Typography sx={{ fontSize: '0.68rem', color: '#94a3b8' }}>
                        Detections inside the project, or up to{' '}
                        {formatDistance(shownRadius, unitSystem)} beyond its boundary,
                        alert everyone subscribed below.
                    </Typography>
                </Box>
            </Box>

            {usableKinds.length === 0 ? (
                <Typography sx={{ color: '#94a3b8', fontSize: '0.85rem' }}>
                    No detection types are available for this project, so nobody
                    can be subscribed yet.
                </Typography>
            ) : (
                <ProjectRecipientsTable
                    users={users}
                    kinds={usableKinds}
                    canManage={can_manage}
                    currentUserId={user?.id}
                    onUpdate={patchUser}
                />
            )}

            {blockedKinds.length > 0 && (
                <Typography sx={{ mt: 1.5, fontSize: '0.72rem', color: '#64748b' }}>
                    Not shown:{' '}
                    {blockedKinds
                        .map(
                            ({ kind, label }) =>
                                `${label} (${
                                    configured_kinds[kind] === false
                                        ? NO_SOURCE_NOTE
                                        : NOT_ENABLED_NOTE
                                })`
                        )
                        .join('; ')}
                    .
                </Typography>
            )}

            <Snackbar
                open={!!snackbar}
                autoHideDuration={6000}
                onClose={() => setSnackbar('')}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
            >
                <Alert
                    onClose={() => setSnackbar('')}
                    severity="error"
                    variant="filled"
                    sx={{ width: '100%', backgroundColor: '#ef5350', color: '#000' }}
                >
                    {snackbar}
                </Alert>
            </Snackbar>
        </Box>
    );
}
